import React, { useState, useEffect } from 'react';
import { XMarkIcon, TruckIcon } from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import { DeliveryOption, DeliveryOptionCreate } from '@/types';
import toast from 'react-hot-toast';

interface DeliveryOptionFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: DeliveryOptionCreate) => Promise<void>;
  editingOption?: DeliveryOption | null;
  title?: string;
}

const DeliveryOptionForm: React.FC<DeliveryOptionFormProps> = ({
  isOpen,
  onClose,
  onSave,
  editingOption,
  title = 'Add Delivery Zone',
}) => {
  const [zoneName, setZoneName] = useState('');
  const [isFreeDelivery, setIsFreeDelivery] = useState(false);
  const [deliveryCharge, setDeliveryCharge] = useState('');
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editingOption) {
      setZoneName(editingOption.zone_name || '');
      setIsFreeDelivery(editingOption.is_free_delivery || false);
      setDeliveryCharge(editingOption.is_free_delivery ? '0' : String(editingOption.delivery_charge || ''));
      setDisplayOrder(editingOption.display_order || 0);
    } else {
      // Reset form for new entry
      setZoneName('');
      setIsFreeDelivery(false);
      setDeliveryCharge('');
      setDisplayOrder(0);
    }
    setErrors({});
  }, [editingOption, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!zoneName.trim()) {
      newErrors.zoneName = 'Zone name is required';
    }

    if (!isFreeDelivery) {
      const charge = parseFloat(deliveryCharge);
      if (!deliveryCharge.trim()) {
        newErrors.deliveryCharge = 'Delivery charge is required when not free delivery';
      } else if (isNaN(charge) || charge < 0) {
        newErrors.deliveryCharge = 'Delivery charge must be a positive number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const data: DeliveryOptionCreate = {
        zone_name: zoneName.trim(),
        delivery_charge: isFreeDelivery ? 0 : parseFloat(deliveryCharge),
        is_free_delivery: isFreeDelivery,
        display_order: displayOrder,
      };

      await onSave(data);
      toast.success(editingOption ? 'Delivery option updated successfully' : 'Delivery option added successfully');
      onClose();
    } catch (error: any) {
      const errorMessage = error?.detail || error?.message || 'Failed to save delivery option';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFreeDeliveryChange = (checked: boolean) => {
    setIsFreeDelivery(checked);
    if (checked) {
      setDeliveryCharge('0');
      // Clear delivery charge error if free delivery is enabled
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.deliveryCharge;
        return newErrors;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <TruckIcon className="h-6 w-6 text-primary-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">{title}</h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
                disabled={isSaving}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Zone Name */}
              <div>
                <label htmlFor="zoneName" className="block text-sm font-medium text-gray-700 mb-1">
                  Zone Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="zoneName"
                  value={zoneName}
                  onChange={(e) => setZoneName(e.target.value)}
                  placeholder="e.g., Within Dhaka, Outside Dhaka"
                  className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500 ${
                    errors.zoneName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSaving}
                />
                {errors.zoneName && (
                  <p className="mt-1 text-xs text-red-500">{errors.zoneName}</p>
                )}
              </div>

              {/* Free Delivery Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="freeDelivery"
                  checked={isFreeDelivery}
                  onChange={(e) => handleFreeDeliveryChange(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  disabled={isSaving}
                />
                <label htmlFor="freeDelivery" className="ml-2 text-sm text-gray-700">
                  Free Delivery
                </label>
              </div>

              {/* Delivery Charge */}
              <div>
                <label htmlFor="deliveryCharge" className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Charge (tk) {!isFreeDelivery && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="number"
                  id="deliveryCharge"
                  value={deliveryCharge}
                  onChange={(e) => setDeliveryCharge(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  disabled={isFreeDelivery || isSaving}
                  className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500 ${
                    errors.deliveryCharge ? 'border-red-500' : 'border-gray-300'
                  } ${isFreeDelivery ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                />
                {errors.deliveryCharge && (
                  <p className="mt-1 text-xs text-red-500">{errors.deliveryCharge}</p>
                )}
                {isFreeDelivery && (
                  <p className="mt-1 text-xs text-gray-500">Delivery charge is set to 0 for free delivery</p>
                )}
              </div>

              {/* Display Order */}
              <div>
                <label htmlFor="displayOrder" className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  id="displayOrder"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                  min="0"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                  disabled={isSaving}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Lower numbers appear first. Use 0 for default ordering.
                </p>
              </div>
            </div>

            {/* Help Text */}
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-xs text-blue-800">
                <strong>Tip:</strong> Add multiple delivery zones to give buyers options. For example, "Within Dhaka: 60 tk" and "Outside Dhaka: 120 tk".
              </p>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              {isSaving ? 'Saving...' : editingOption ? 'Update' : 'Add Zone'}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
              className="mt-3 sm:mt-0 w-full sm:w-auto"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryOptionForm;
